import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, OnDestroy, Output, SimpleChanges, NgZone, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMapsModule } from '@angular/google-maps';
import { RouterModule } from '@angular/router';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { IClusterRenderer } from '../../interfaces/maps/base-cluster-renderer';
import { IMarkerFactory } from '../../interfaces/maps/base-marker-factory';
import { DefaultClusterRender } from '../../helpers/maps/default-cluster-renderer.helper';
import { DefaultMarkerFactory } from '../../helpers/maps/default-marker-factory.helper';
import { IMapInfoWindowBuilder } from '../../interfaces/maps/base-map-info-window-builder';
import { SearchComponent } from '../filters/search/search.component';

@Component({
  selector: 'app-common-map',
  standalone: true,
  imports: [GoogleMapsModule, RouterModule, CommonModule, SearchComponent],
  templateUrl: './common-map.component.html',
  styleUrl: './common-map.component.scss'
})
export class CommonMap implements OnInit, AfterViewInit, OnDestroy {
  @Input() mapCenter: google.maps.LatLngLiteral | null = null;
  @Input() focusPositions: google.maps.LatLngLiteral[] = [];
  @Input() mapHeight: string;
  @Input() routeLink: string;
  @Input() isCollapsed: boolean = true;
  @Input() showDropdown: boolean = true;
  @Input() data: any[] = [];
  @Input() dropdownItems: string[] = [];
  @Input() selectedItem: string = '';
  @Input() clusterRenderer: IClusterRenderer;
  @Input() markerFactory: IMarkerFactory;
  @Input() mapInfoWindowBuilder: IMapInfoWindowBuilder;
  @Input() mapTypeControl: boolean = false;
  @Input() keyboardShortcuts: boolean = true;
  @Input() canExpandMap: boolean = true;
  @Input() disableClustering: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() disableControlsWhileLoading: boolean = true;
  @Input() mapTypeId: string = 'roadmap';
  @Input() mapId: string = 'WEBTRACK_FUEL_MAP';
  @Input() currencySymbol: string = '';
  @Input() showMapLayerToggle: boolean = false;
  @Input() mapLayerWidth: string | null = null;
  @Input() mapLayerHeight: string | null = null;
  @Input() show360ViewControl: boolean = false;
  @Input() showMapSearch: boolean = false;
  @Input() mapSearchTerm: string = '';
  @Input() mapSearchPlaceholder: string = 'Search';
  @Input() mapSearchFields: string[] = [];
  @Input() mapSearchWidth: string = '300px';
  @Input() mapSearchRight: string = '16px';
  @Input() expandQueryParams: { [key: string]: string | boolean } = {};
  // Optional cap so fitBounds on a single/tight marker set doesn't zoom to max.
  @Input() fitMaxZoom: number | null = null;
  @Output() dropdownChange = new EventEmitter<string>();
  @Output() markerClicked = new EventEmitter<any>();
  @Output() mapSearchTermUpdated = new EventEmitter<string>();
  @Output() filteredDataChange = new EventEmitter<any[]>();

  currentMapType: string = 'roadmap';
  layerPreviewUrl: string = '';
  markersLoading: boolean = false;
  isDropdownOpen: boolean = false;
  advancedMarkerOptions: google.maps.marker.AdvancedMarkerElementOptions = { gmpDraggable: false };
  advancedMarkerPositions: (google.maps.LatLngLiteral & { Areas: any })[] = [];
  markers: google.maps.marker.AdvancedMarkerElement[] = [];
  filteredData: any[] = [];
  map!: google.maps.Map;
  markerCluster!: MarkerClusterer;
  options: google.maps.MapOptions = {};

  private reloadToken = 0;
  private mapTypeListener: google.maps.MapsEventListener | null = null;
  private readonly BATCH_SIZE = 500;
  private readonly IDLE_BUDGET_MS = 24;
  private idleListener: google.maps.MapsEventListener | null = null;
  queryParams: { [key: string]: string | boolean };

  get controlsDisabled(): boolean {
    return this.disableControlsWhileLoading && (this.isLoading || this.markersLoading);
  }

  constructor(private zone: NgZone, private elementRef: ElementRef, private cdr: ChangeDetectorRef) {
    this.markerFactory = this.markerFactory ?? new DefaultMarkerFactory();
    this.clusterRenderer = this.clusterRenderer ?? new DefaultClusterRender();
  }

  ngOnInit(): void {
    if (this.dropdownItems?.length) {
      this.selectedItem = this.selectedItem || this.dropdownItems[0];
      this.dropdownChange.emit(this.selectedItem);
    }

    // Set query parameters to indicate if the main page is being loaded after returning from the maps page
    this.queryParams = this.isCollapsed ? this.expandQueryParams : { fromMap: 'true' }

    this.applyMapSearch();

    this.options = {
      center: { lat: 0, lng: 0 },
      zoom: 0,
      fullscreenControl: false,
      mapTypeControl: this.mapTypeControl,
      zoomControl: true,
      cameraControl: false,
      keyboardShortcuts: this.keyboardShortcuts,
      streetViewControl: this.show360ViewControl,
      mapTypeId: this.mapTypeId,
      mapId: this.mapId,
      restriction: {
        latLngBounds: { north: 85, south: -85, east: 179.999, west: -179.999 },
        strictBounds: true
      }
    };
  }

  ngAfterViewInit(): void {
    const dropdownElement = this.elementRef.nativeElement.querySelector('#dropdown-button');
    if (dropdownElement) {
      dropdownElement.addEventListener('shown.bs.dropdown', () => {
        this.isDropdownOpen = true;
      });
      dropdownElement.addEventListener('hidden.bs.dropdown', () => {
        this.isDropdownOpen = false;
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mapCenter'] && this.mapCenter && this.map) {
      this.map.panTo(this.mapCenter);
      this.map.setZoom(18);
    }

    if (changes['focusPositions'] && this.map) {
      this.focusOnPositions(this.focusPositions);
    }

    if (changes['data'] || changes['mapSearchTerm']) {
      this.applyMapSearch();
    }

    if ((changes['data'] || changes['mapSearchTerm']) && this.map) {
      if (changes['data'] && (!this.mapCenter || (this.mapCenter.lat === 0 && this.mapCenter.lng === 0))) {
        this.map.setZoom(0);
      }

      if (!this.filteredData.length) {
        this.clearMarkersImmediately();
        return;
      }

      if (!this.isLoading) this.reloadMarkers();
    }

    if (changes['isLoading'] && !this.isLoading && !changes['data'] && !changes['mapSearchTerm'] && this.map) {
      if (!this.filteredData.length) {
        this.clearMarkersImmediately();
        return;
      }

      this.reloadMarkers();
    }
  }

  onMapInit(map: google.maps.Map): void {
    this.map = map;

    this.mapTypeListener = this.map.addListener('maptypeid_changed', () => {
      const currentMapType = this.map.getMapTypeId();
      if (currentMapType) {
        localStorage.setItem('webtrack-map-type', currentMapType);
      }
    });

    this.applyMapSearch();

    if (!this.isLoading) this.reloadMarkers();

    if (this.showMapLayerToggle) {
      this.updateLayerPreview();
      this.idleListener = this.map.addListener('idle', () => {
        this.zone.run(() => {
          this.updateLayerPreview();
          this.cdr.markForCheck();
        });
      });
    }
  }

  async initCluster(): Promise<void> {
    if (this.disableClustering) {
      this.markers.forEach(marker => {
        marker.map = this.map;
      });

      return;
    }

    if (this.markerCluster) {
      (this.markerCluster as any).setMap?.(null);
      this.markerCluster.clearMarkers(true);
    }

    this.markerCluster = new MarkerClusterer({
      map: this.map,
      markers: this.markers,
      renderer: this.clusterRenderer
    });

    this.markerCluster.render();
  }

  async reloadMarkers(): Promise<void> {
    if (!this.map) return;
    const token = ++this.reloadToken; // cancel previous run if any

    this.setMarkersLoading(true);

    try {
      // Do heavy work outside Angular
      await this.zone.runOutsideAngular(async () => {
        this.clearMarkers();

        if (!this.filteredData?.length) {
          this.initCluster();
          return;
        }

        const sharedInfoWindow = new google.maps.InfoWindow();
        this.map.addListener('click', () => sharedInfoWindow.close());

        // Build & add in batches
        await this.processInBatches(this.filteredData, this.BATCH_SIZE, async (batch) => {
          // If a new reload started, bail early
          if (token !== this.reloadToken) return;

          const batchMarkers = await Promise.all(
            batch.map(async (location) => {
              const latitude = Number(location.lat ?? location.latitude);
              const longitude = Number(location.lng ?? location.longitude);

              if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

              const marker = await this.markerFactory.createMarker(undefined, latitude, longitude, location);
              marker.dataset['id'] = crypto.randomUUID();

              // Emit selection on every marker click, independent of whether an
              // info window is opened or toggled closed below.
              marker.addListener('click', () => {
                this.zone.run(() => this.markerClicked.emit(location));
              });

              this.mapInfoWindowBuilder?.buildMapInfoWindow(location, marker, this.currencySymbol);
              marker.addListener("map-info-window-open", (event: { id: string, infoWindowContent: string }) => {
                if (event.id !== marker.dataset['id'] || (sharedInfoWindow.isOpen && event.id === marker.dataset['id'])) {
                  sharedInfoWindow.close();
                  return;
                }

                this.map.setZoom(18);
                this.map.setCenter({ lat: latitude, lng: longitude });

                sharedInfoWindow.setContent(event.infoWindowContent);
                sharedInfoWindow.open({ map: this.map, anchor: marker as any });
              });

              return marker;
            })
          );

          const markers = batchMarkers.filter(Boolean) as google.maps.marker.AdvancedMarkerElement[];
          if (markers.length) this.markers.push(...markers);
        });

        if (token !== this.reloadToken) return;

        this.initCluster();
        if (this.focusPositions?.length) {
          this.focusOnPositions(this.focusPositions);
        } else {
          this.fitBoundsToMarkers();
        }
      });
    } finally {
      if (token === this.reloadToken) this.setMarkersLoading(false);
    }
  }

  onSelect(item: string) {
    this.selectedItem = item;
    this.dropdownChange.emit(this.selectedItem);
    this.isDropdownOpen = false;
  }

  onExpandClick(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  onMapSearchTermUpdated(searchTerm: string): void {
    this.mapSearchTerm = searchTerm;
    this.mapSearchTermUpdated.emit(this.mapSearchTerm);
    this.applyMapSearch();

    if (!this.map || this.isLoading) return;

    if (!this.filteredData.length) {
      this.clearMarkersImmediately();
      return;
    }

    void this.reloadMarkers();
  }

  ngOnDestroy(): void {
    this.mapTypeListener?.remove();
    this.mapTypeListener = null;

    if (this.idleListener) {
      this.idleListener.remove();
      this.idleListener = null;
    }

  }

  toggleMapLayer(): void {
    this.currentMapType = this.currentMapType === 'roadmap' ? 'satellite' : 'roadmap';
    if (this.map) {
      this.map.setMapTypeId(this.currentMapType);
    }
    this.updateLayerPreview();
  }

  private updateLayerPreview(): void {
    if (!this.map) return;

    const center = this.map.getCenter();
    if (!center) return;

    const zoom = Math.min(Math.max(Math.round(this.map.getZoom() ?? 0), 0), 21);
    const lat = center.lat();
    const lng = center.lng();

    // Convert lat/lng to tile coordinates
    const scale = Math.pow(2, zoom);
    const x = ((Math.floor(((lng + 180) / 360) * scale) % scale) + scale) % scale;
    const latRad = (lat * Math.PI) / 180;
    const y = Math.max(0, Math.min(scale - 1, Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * scale)));

    // lyrs: m = roadmap, s = satellite
    const previewLyrs = this.currentMapType === 'roadmap' ? 's' : 'm';
    this.layerPreviewUrl = `https://mt1.google.com/vt/lyrs=${previewLyrs}&x=${x}&y=${y}&z=${zoom}`;
  }

  private applyMapSearch(): void {
    const sourceData = this.data ?? [];
    const normalizedTerm = this.normalizeSearchValue(this.mapSearchTerm).trim();

    this.filteredData = normalizedTerm
      ? sourceData.filter((item) => this.matchesMapSearch(item, normalizedTerm))
      : sourceData;

    const filteredData = this.filteredData;
    queueMicrotask(() => this.zone.run(() => this.filteredDataChange.emit(filteredData)));
  }

  private matchesMapSearch(item: unknown, normalizedTerm: string): boolean {
    const fields = this.mapSearchFields?.filter(Boolean) ?? [];

    if (!fields.length) {
      return this.normalizeSearchValue(item).includes(normalizedTerm);
    }

    return fields.some((field) =>
      this.normalizeSearchValue(this.readFieldValue(item, field)).includes(normalizedTerm)
    );
  }

  private readFieldValue(item: unknown, field: string): unknown {
    if (!item || typeof item !== 'object') return undefined;

    return field.split('.').reduce<unknown>((value, key) => {
      if (!value || typeof value !== 'object') return undefined;
      return (value as Record<string, unknown>)[key];
    }, item);
  }

  private normalizeSearchValue(value: unknown, depth: number = 0): string {
    if (value === null || value === undefined || depth > 2) return '';
    if (value instanceof Date) return value.toISOString().toLowerCase();
    if (Array.isArray(value)) {
      return value.map((item) => this.normalizeSearchValue(item, depth + 1)).join(' ');
    }
    if (typeof value === 'object') {
      return Object.values(value as Record<string, unknown>)
        .map((item) => this.normalizeSearchValue(item, depth + 1))
        .join(' ');
    }

    return String(value).toLowerCase();
  }

  private setMarkersLoading(loading: boolean): void {
    if (loading === this.markersLoading) return;

    this.zone.run(() => {
      this.markersLoading = loading;
      // markForCheck so the spinner toggles even when an OnPush ancestor would
      // otherwise skip this component during the zone-triggered tick.
      this.cdr.markForCheck();
    });
  }

  private focusOnPositions(
    positions: google.maps.LatLngLiteral[] | null | undefined,
    padding: number | google.maps.Padding = 80
  ): void {
    if (!this.map) return;

    const validPositions = (positions ?? []).filter((position) =>
      Number.isFinite(position.lat) && Number.isFinite(position.lng)
    );

    if (!validPositions.length) return;

    if (validPositions.length === 1) {
      this.map.panTo(validPositions[0]);
      this.map.setZoom(18);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    validPositions.forEach((position) => bounds.extend(position));

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, padding);
    }
  }

  private fitBoundsToMarkers(padding: number | google.maps.Padding = 40): void {
    if (!this.markers.length) return;

    const bounds = new google.maps.LatLngBounds();

    for (const m of this.markers) {
      const p = m.position;
      if (!p) continue;

      // AdvancedMarkerElement.position can be LatLng OR LatLngLiteral
      if (typeof (p as any).lat === 'function') {
        bounds.extend(p as google.maps.LatLng);
      } else {
        bounds.extend(p as google.maps.LatLngLiteral);
      }
    }

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, padding);

      if (this.fitMaxZoom != null) {
        const maxZoom = this.fitMaxZoom;
        google.maps.event.addListenerOnce(this.map, 'idle', () => {
          // Re-fit once the container has its final size - a flex-sized map can
          // be unsized on the first fit, which lands on the wrong centre/zoom.
          this.map.fitBounds(bounds, padding);
          google.maps.event.addListenerOnce(this.map, 'idle', () => {
            // Cap the zoom (a single marker would otherwise zoom to max) and
            // re-centre so the capped view stays on the markers.
            const zoom = this.map.getZoom();
            if (zoom != null && zoom > maxZoom) {
              this.map.setZoom(maxZoom);
              this.map.setCenter(bounds.getCenter());
            }
          });
        });
      }
    }
  }

  private clearMarkersImmediately(): void {
    this.reloadToken++;
    this.clearMarkers();
    void this.initCluster();
  }

  private clearMarkers(): void {
    try {
      this.markerCluster.clearMarkers();
      (this.markerCluster as any).setMap?.(null);
    } catch { }
    for (const marker of this.markers) {
      try {
        google.maps.event.clearInstanceListeners(marker);
      } catch { }
      (marker as any).map = null;
    }
    this.markers = [];
  }

  private async nextIdleFrame(timeoutMs = this.IDLE_BUDGET_MS): Promise<void> {
    return new Promise(resolve => {
      const requestIdleCallback = (window as any).requestIdleCallback as
        | ((callBack: () => void, opts?: { timeout: number }) => number)
        | undefined;
      if (requestIdleCallback) requestIdleCallback(() => resolve(), { timeout: timeoutMs });
      else if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(() => resolve());
      else setTimeout(resolve, timeoutMs);
    });
  }

  private async processInBatches<T>(
    items: T[],
    batchSize: number,
    onBatch: (batch: T[], batchIndex: number) => Promise<void> | void
  ): Promise<void> {
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      await onBatch(batch, i / batchSize);
      await this.nextIdleFrame();
    }
  }
}
